import { ApiError } from "../api/client.js";
import type { Receipt, SigningPayload } from "../api/types.js";
import { clock } from "../lib/format.js";
import type { BankCard, CardEnd, EndAction } from "../ui/bankcard.js";
import { CodeEntry } from "../ui/code.js";
import { cancelLabel, stepUpCopy, type Line } from "./notices.js";
import { confirmParts } from "./schedule-words.js";

/**
 * 05 step-up after the signature, in the contract's order (UX spec v3 §6.5,
 * ruling 23 Sep): the gateway answered `awaiting_step_up`, so the SAME card
 * turns Signed and asks for the token code by a clock time — never a countdown.
 *
 *  - Confirm sends the code once (an in-flight guard; a second tap is swallowed);
 *    the button keeps its label and holds busy while the bank checks.
 *  - A wrong code shows the bank's own sentence (`STEP_UP_INVALID`), three wrong
 *    codes end it (`STEP_UP_EXHAUSTED`), and so does the end of the window —
 *    measured from the receipt's arrival on this device, one second early, the
 *    way the card's own life is measured.
 *  - Cancel is asked inside the card (no modal, no signature: stopping money is
 *    the fast path) and goes to the gateway's one cancel route.
 *  - "Can't find your token? Ask for a call" (rm-callback, DCTA-74 ask r) moves the
 *    SAME factor to the bank's call — same window, same bytes, the signature not
 *    asked again. The card has no code field and no Confirm then: the call is the
 *    second factor, and the card learns it happened from the bank's own receipt
 *    read (`GET /v1/gateway/bundles/{id}`), polled while it waits. "Use my token
 *    instead" moves it back.
 */
export interface StepUpDeps {
  readonly api: {
    stepUp(bundleId: string, code: string): Promise<Receipt>;
    cancelBundle(bundleId: string, reason: string): Promise<unknown>;
    stepUpMethod(bundleId: string, method: "rm_callback" | "hardware_token"): Promise<unknown>;
    receipt(bundleId: string): Promise<Receipt>;
  };
  readonly card: BankCard;
  readonly payload: SigningPayload;
  readonly receipt: Receipt;
  /** The BANK's clock (DCTA-85 P1-2): `undefined` until the bank has said its time. */
  readonly now: () => Date | undefined;
  /** How often the card asks the bank whether the call has happened (rm-callback). */
  readonly pollMs?: number;
  /** One line from the assistant, handing over to the card. */
  readonly say: (line: string) => void;
  /** The code went through: the receipt of what ran. */
  readonly done: (receipt: Receipt) => void;
  /** "Start again": the same words, asked again. */
  readonly again: () => void;
  /** Anything the card has no words for goes to the chat's error path. */
  readonly failed: (error: unknown) => void;
}

type Phase = "waiting" | "checking" | "switching" | "calling" | "cancel" | "cancelling" | "ended";

/** What a receipt read says once the call has been taken: something ran or waits as signed. */
const RAN: ReadonlySet<string> = new Set(["executed", "partially_executed", "held"]);
/** …or nothing will: the bank stopped it. */
const STOPPED: ReadonlySet<string> = new Set(["not_executed", "cancelled_by_customer"]);

export class StepUpFlow {
  readonly #d: StepUpDeps;
  readonly #copy: ReturnType<typeof stepUpCopy>;
  /** Confirm's words: the figure waiting — or, on a schedule card, the sign label's words (never the day's bill). */
  readonly #label: { readonly money: string; readonly rest?: string };
  readonly #code: CodeEntry;
  readonly #by: string;
  readonly #count: string;
  #phase: Phase = "waiting";
  #timer: ReturnType<typeof setTimeout> | undefined;
  #poll: ReturnType<typeof setInterval> | undefined;
  /** Where "Keep it" goes back to: the code, or the call. */
  #kept: "waiting" | "calling" = "waiting";
  /** The window closed while a switch was on its way: its answer ends the card. */
  #late = false;

  constructor(deps: StepUpDeps) {
    this.#d = deps;
    // DCTA-143: the card's bundle carries the bank's flags — a flagged name is said in its inert form here too.
    this.#copy = stepUpCopy(deps.payload, deps.receipt, deps.card.bundle.firewall?.data_flags ?? []);
    const schedule = deps.payload.schedule;
    const step = deps.payload.steps[0];
    this.#label = schedule !== undefined && step !== undefined ? confirmParts(schedule, step) : { money: this.#copy.amount };
    const n = deps.payload.steps.length;
    this.#count = n === 1 ? "1 operation" : `${String(n)} operations`;
    const windowMs = windowOf(deps.receipt.step_up, deps.now());
    this.#by = deadline(deps.receipt.step_up?.expires_at, deps.now(), windowMs);
    this.#code = new CodeEntry(() => this.#syncPrimary());
    this.#timer = setTimeout(() => this.#expire(false), Math.max(0, windowMs - 1000));
    this.#waiting();
  }

  /** QA-04: still waiting for its code (or the call) — a reload draws it again; an ended one is the past. */
  get open(): boolean {
    return this.#phase !== "ended";
  }

  dispose(): void {
    if (this.#timer !== undefined) clearTimeout(this.#timer);
    this.#timer = undefined;
    this.#stopPolling();
  }

  #stopPolling(): void {
    if (this.#poll !== undefined) clearInterval(this.#poll);
    this.#poll = undefined;
  }

  // ------------------------------------------------------------------ phases
  #waiting(): void {
    this.#phase = "waiting";
    this.#d.card.phase({
      status: "Signed",
      pos: true,
      label: `Bank: ${this.#count}, signed, waiting for your security token`,
      leaves: true,
      notices: [{ icon: "i-clock", cls: "wait", ...this.#copy.waiting(this.#by) }],
      extra: this.#code.element,
      primary: { verb: "Confirm", ...this.#label, icon: "i-token", onSelect: () => this.#confirm() },
      acts: [
        { label: "Can't find your token? Ask for a call", kind: "link", onSelect: () => this.#switch("rm_callback") },
        { label: cancelLabel(this.#d.payload.steps), kind: "link", onSelect: () => this.#askCancel() },
      ],
    });
    this.#code.disabled(false);
    this.#syncPrimary();
  }

  #syncPrimary(): void {
    if (this.#phase !== "waiting" && this.#phase !== "checking") return;
    const checking = this.#phase === "checking";
    const ready = this.#code.digits.length === 6 && !this.#code.input.classList.contains("bad");
    this.#d.card.primary({
      disabled: !ready,
      busy: checking,
      why: checking ? "Checking the code" : ready ? undefined : "Enter the code to confirm",
    });
  }

  #confirm(): void {
    if (this.#phase !== "waiting" || this.#code.digits.length !== 6) return;
    this.#phase = "checking";
    this.#code.disabled(true);
    this.#syncPrimary();
    this.#d.api
      .stepUp(this.#d.receipt.bundle_id, this.#code.digits)
      .then((receipt) => {
        this.dispose();
        this.#phase = "ended";
        this.#d.card.phase(null);
        this.#d.done(receipt);
      })
      .catch((error: unknown) => {
        const code = error instanceof ApiError ? error.reasonCode : "";
        if (code === "STEP_UP_INVALID") {
          this.#phase = "waiting";
          this.#code.disabled(false);
          this.#code.bad((error as ApiError).serverMessage ?? "That code didn't match.");
          this.#syncPrimary();
          this.#code.input.focus();
          return;
        }
        if (code === "STEP_UP_EXHAUSTED") {
          this.#end(this.#copy.exhausted, "i-alert", "That was cancelled. Say it again when you have your token.", true);
          return;
        }
        if (code === "CHALLENGE_EXPIRED") {
          this.#expire(true);
          return;
        }
        this.#phase = "waiting";
        this.#code.disabled(false);
        this.#syncPrimary();
        this.#d.failed(error);
      });
  }

  // ------------------------------------------------------------ the call
  /**
   * The factor's channel, switched at the bank: once (the phase guards a second
   * tap), and the card changes only when the bank has said yes. A refusal keeps
   * the card where it was and the chat says why in the bank's words.
   */
  #switch(method: "rm_callback" | "hardware_token"): void {
    const from = this.#phase;
    if ((method === "rm_callback" && from !== "waiting") || (method === "hardware_token" && from !== "calling")) return;
    this.#phase = "switching";
    if (method === "hardware_token") this.#stopPolling();
    this.#d.api
      .stepUpMethod(this.#d.receipt.bundle_id, method)
      .then(() => {
        if (this.#phase !== "switching") return;
        if (method === "rm_callback") this.#calling();
        else this.#waiting();
        if (this.#late) this.#expire(true);
      })
      .catch((error: unknown) => {
        if (this.#phase !== "switching") return;
        if (from === "calling") this.#calling();
        else this.#phase = "waiting";
        if (this.#late) this.#expire(true);
        else this.#d.failed(error);
      });
  }

  #calling(): void {
    this.#phase = "calling";
    this.#d.card.phase({
      status: "Signed",
      pos: true,
      label: `Bank: ${this.#count}, signed, waiting for the bank's call`,
      leaves: true,
      notices: [{ icon: "i-phone", cls: "wait", ...this.#copy.calling }],
      acts: [
        { label: "Use my token instead", kind: "ghost", onSelect: () => this.#switch("hardware_token") },
        { label: cancelLabel(this.#d.payload.steps), kind: "link", onSelect: () => this.#askCancel() },
      ],
    });
    if (this.#poll === undefined) this.#poll = setInterval(() => this.#check(), this.#d.pollMs ?? 4000);
  }

  /** Has the bank taken the call? Its own receipt read says; nothing on the client decides. */
  #check(): void {
    if (this.#phase !== "calling") return;
    this.#d.api
      .receipt(this.#d.receipt.bundle_id)
      .then((r) => {
        if (this.#phase !== "calling") return;
        if (RAN.has(r.state)) {
          this.dispose();
          this.#phase = "ended";
          this.#d.card.phase(null);
          this.#d.done(r);
        } else if (STOPPED.has(r.state)) {
          this.#expire(true);
        }
      })
      .catch(() => undefined); // a missed read is asked again at the next beat
  }

  #askCancel(): void {
    if (this.#phase !== "waiting" && this.#phase !== "calling") return;
    this.#kept = this.#phase;
    this.#stopPolling();
    this.#phase = "cancel";
    this.#d.card.phase({
      status: "Signed",
      pos: true,
      label: `Bank: ${this.#count}, signed — cancel it?`,
      leaves: true,
      notices: [{ icon: "i-clock", cls: "end", ...this.#copy.cancelAsk }],
      acts: [
        { label: "Cancel it", kind: "btn", onSelect: () => this.#cancel() },
        { label: "Keep it", kind: "ghost", onSelect: () => (this.#kept === "calling" ? this.#calling() : this.#waiting()) },
      ],
    });
  }

  #cancel(): void {
    if (this.#phase !== "cancel") return;
    this.#phase = "cancelling";
    this.#d.api
      .cancelBundle(this.#d.receipt.bundle_id, "customer cancelled while waiting for the security token")
      .then(() => {
        const now = this.#d.now();
        const at = now === undefined ? undefined : clock(now.toISOString());
        this.#end(this.#copy.cancelled(at), "i-alert", this.#copy.partial ? "Cancelled." : "Cancelled. Nothing was sent.", false);
      })
      .catch((error: unknown) => {
        // Not cancelled: the question stays, and the chat says why in the bank's words.
        this.#phase = this.#kept;
        this.#askCancel();
        this.#d.failed(error);
      });
  }

  /**
   * The window closed. A timer firing while a code or a cancel is in flight
   * waits for the bank's answer instead (that answer decides); the gateway's own
   * CHALLENGE_EXPIRED arrives here from the checking phase.
   */
  #expire(fromGateway = false): void {
    if (this.#phase === "ended" || this.#phase === "cancelling") return;
    if (this.#phase === "checking" && !fromGateway) return;
    if (this.#phase === "switching" && !fromGateway) {
      this.#late = true;
      return;
    }
    const call = this.#phase === "calling" || (this.#phase === "cancel" && this.#kept === "calling");
    this.#end(call ? this.#copy.noCall(this.#by) : this.#copy.expired(this.#by), "i-clock", this.#copy.partial ? "That ran out of time." : "That ran out of time. Nothing was sent.", true);
    // The factor is spent either way; the bank is told, so nothing waits on it.
    void this.#d.api.cancelBundle(this.#d.receipt.bundle_id, "no security token code within the window").catch(() => undefined);
  }

  #end(line: Line, glyph: "i-alert" | "i-clock", said: string, again: boolean): void {
    this.dispose();
    this.#phase = "ended";
    const acts: EndAction[] = again ? [{ label: "Start again", kind: "link", onSelect: () => this.#d.again() }] : [];
    const end: CardEnd = { status: "Not sent", label: "Bank: not sent", leaves: false, notices: [{ icon: glyph, cls: "end", ...line }], acts };
    this.#d.card.phase(end);
    this.#d.say(said);
  }
}

/**
 * How long the code is still taken: the confirm's own count (`expires_in_seconds`); else — the receipt READ, as a reload
 * draws the card again (QA-04) — what is left of the bank's own record (`expires_at`) on the bank's clock; else 15 minutes.
 */
export function windowOf(stepUp: Receipt["step_up"], bankNow: Date | undefined): number {
  if (stepUp?.expires_in_seconds !== undefined) return stepUp.expires_in_seconds * 1000;
  const at = stepUp?.expires_at === undefined ? Number.NaN : Date.parse(stepUp.expires_at);
  if (bankNow !== undefined && !Number.isNaN(at)) return Math.max(0, at - bankNow.getTime());
  return 900_000;
}

/**
 * When the bank stops accepting the code (DCTA-85 P1-2), in the order of trust: the bank's own
 * step-up record (`expires_at`), else the bank's clock plus the bank's window, else — before the
 * bank has said its time — the window itself ("within 15 minutes"). Never the phone's clock.
 */
export function deadline(expiresAt: string | undefined, bankNow: Date | undefined, windowMs: number): string {
  if (expiresAt !== undefined && !Number.isNaN(Date.parse(expiresAt))) return `by ${clock(expiresAt)}`;
  if (bankNow !== undefined) return `by ${clock(new Date(bankNow.getTime() + windowMs).toISOString())}`;
  const minutes = Math.max(1, Math.round(windowMs / 60_000));
  return `within ${String(minutes)} ${minutes === 1 ? "minute" : "minutes"}`;
}
